// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * IntakePage - /intake
 * Moduleformulier voor persoonlijke gegevens, partner, kinderen, huisdieren en woning.
 *
 * Gebruikt DEZELFDE opslagsleutels als de wizard-stappen:
 *   partner  → loadPartner / savePartner
 *   children → loadChildren / saveChildren
 *   pets     → loadPets / savePets
 *   housing  → loadHousing / saveHousing
 *
 * Hierdoor zijn data ingevuld via de wizard en via IntakePage altijd identiek.
 * NabestaandenDossier leest uit dezelfde bronnen en toont altijd correcte data.
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReturnNav } from '@/lib/returnNav'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { CheckCircle2 } from 'lucide-react'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { Input, Textarea } from '@/components/ui'
import { RadioGroup }      from '@/components/ui/RadioGroup'
import { Card }            from '@/components/ui/Card'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { useAutosave }     from '@/hooks/useAutosave'
import { saveModuleProgress } from '@/services/profile'
import {
  loadPartner,  savePartner,  EMPTY_PARTNER,  type PartnerData,
  loadChildren, saveChildren, EMPTY_CHILDREN, type ChildrenData,
  loadPets,     savePets,     EMPTY_PETS,     type PetsData,
  loadHousing,  saveHousing,  EMPTY_HOUSING,  type HousingData,
} from '@/services/wizard'
import type { SaveStatus } from '@/hooks/useAutosave'

const YES_NO   = [{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }]
const HOUSING  = [
  { value: 'owner',  label: 'Koopwoning' },
  { value: 'rental', label: 'Huurwoning' },
  { value: 'social', label: 'Sociale huurwoning' },
  { value: 'other',  label: 'Anders' },
]

// ── Partner section ────────────────────────────────────────────────────────────
function PartnerSection({ data, onChange, status }: {
  data: PartnerData
  onChange: (d: PartnerData) => void
  status: SaveStatus
}) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-ink">Partner</h2>
        <SaveStatusBadge status={status} />
      </div>
      <div className="flex flex-col gap-3">
        <RadioGroup name="hasPartner" label="Heeft u een partner of echtgenoot/e?"
          options={YES_NO} value={data.hasPartner}
          onChange={v => onChange({ ...data, hasPartner: v })} />
        {data.hasPartner === 'yes' && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Voornaam" value={data.name}
                onChange={e => onChange({ ...data, name: e.target.value })}
                placeholder="Voornaam partner" />
              <Input label="Achternaam" value={data.lastName}
                onChange={e => onChange({ ...data, lastName: e.target.value })}
                placeholder="Achternaam partner" />
            </div>
            <Input label="Geboortedatum" type="date" value={data.dob}
              onChange={e => onChange({ ...data, dob: e.target.value })} />
            <Input label="Relatievorm" value={data.relationForm}
              onChange={e => onChange({ ...data, relationForm: e.target.value })}
              placeholder="Bijv: getrouwd, samenwonend, geregistreerd partnerschap" />
            <Textarea label="Aanvullende opmerkingen" value={data.notes}
              onChange={e => onChange({ ...data, notes: e.target.value })}
              placeholder="Bijv: relevante informatie over uw relatie voor uw naasten" />
          </>
        )}
      </div>
    </Card>
  )
}

// ── Children section ───────────────────────────────────────────────────────────
function ChildrenSection({ data, onChange, status }: {
  data: ChildrenData
  onChange: (d: ChildrenData) => void
  status: SaveStatus
}) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-ink">Kinderen</h2>
        <SaveStatusBadge status={status} />
      </div>
      <div className="flex flex-col gap-3">
        <RadioGroup name="hasChildren" label="Heeft u kinderen?"
          options={YES_NO} value={data.hasChildren}
          onChange={v => onChange({ ...data, hasChildren: v })} />
        {data.hasChildren === 'yes' && (
          <>
            <p className="text-xs text-ink-4">
              {data.children.length > 0
                ? `${data.children.length} ${data.children.length === 1 ? 'kind' : 'kinderen'} geregistreerd via de wizard.`
                : 'Voeg kinderen toe via de begeleide wizard voor uitgebreide informatie per kind.'}
            </p>
            <Textarea label="Aanvullende opmerkingen over uw kinderen" value={data.guardianNotes}
              onChange={e => onChange({ ...data, guardianNotes: e.target.value })}
              placeholder="Bijv: bijzonderheden per kind, schoolgegevens, contactinfo" />
          </>
        )}
      </div>
    </Card>
  )
}

// ── Pets section ───────────────────────────────────────────────────────────────
function PetsSection({ data, onChange, status }: {
  data: PetsData
  onChange: (d: PetsData) => void
  status: SaveStatus
}) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-ink">Huisdieren</h2>
        <SaveStatusBadge status={status} />
      </div>
      <div className="flex flex-col gap-3">
        <RadioGroup name="hasPets" label="Heeft u huisdieren?"
          options={YES_NO} value={data.hasPets}
          onChange={v => onChange({ ...data, hasPets: v })} />
        {data.hasPets === 'yes' && (
          <p className="text-xs text-ink-4">
            {data.pets.length > 0
              ? `${data.pets.length} huisdier(en) geregistreerd via de wizard.`
              : 'Voeg huisdieren toe via de begeleide wizard voor uitgebreide informatie.'}
          </p>
        )}
      </div>
    </Card>
  )
}

// ── Housing section ────────────────────────────────────────────────────────────
function HousingSection({ data, onChange, status }: {
  data: HousingData
  onChange: (d: HousingData) => void
  status: SaveStatus
}) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-ink">Woning</h2>
        <SaveStatusBadge status={status} />
      </div>
      <div className="flex flex-col gap-3">
        <RadioGroup name="housingType" label="Soort woning"
          options={HOUSING} value={data.type}
          onChange={v => onChange({ ...data, type: v })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Straat" value={data.street ?? ''}
            onChange={e => onChange({ ...data, street: e.target.value })}
            placeholder="Straatnaam" />
          <Input label="Huisnummer" value={data.houseNumber ?? ''}
            onChange={e => onChange({ ...data, houseNumber: e.target.value })}
            placeholder="12A" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Postcode" value={data.postalCode ?? ''}
            onChange={e => onChange({ ...data, postalCode: e.target.value })}
            placeholder="1234 AB" />
          <Input label="Woonplaats" value={data.city ?? ''}
            onChange={e => onChange({ ...data, city: e.target.value })}
            placeholder="Amsterdam" />
        </div>
        {data.type === 'owner' && (
          <Input label="Hypotheekverstrekker" value={data.mortgage}
            onChange={e => onChange({ ...data, mortgage: e.target.value })}
            placeholder="Bijv: ING Hypotheken, polisnr. 123456" />
        )}
        <Input label="Reservesleutels en toegang" value={data.keys}
          onChange={e => onChange({ ...data, keys: e.target.value })}
          placeholder="Bijv: bij buurvrouw Van Dam" />
      </div>
    </Card>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function IntakePage() {
  const { userId } = useAuthStore()
  const navigate   = useNavigate()
  const { target, label } = useReturnNav()
  const [isLoading, setIsLoading] = useState(true)

  // Separate state per entity - each saves to its own wizard service key
  const [partner,  setPartner]  = useState<PartnerData>(EMPTY_PARTNER)
  const [children, setChildren] = useState<ChildrenData>(EMPTY_CHILDREN)
  const [pets,     setPets]     = useState<PetsData>(EMPTY_PETS)
  const [housing,  setHousing]  = useState<HousingData>(EMPTY_HOUSING)

  // Load all on mount
  useEffect(() => {
    if (!userId) return
    Promise.all([
      loadPartner(userId),
      loadChildren(userId),
      loadPets(userId),
      loadHousing(userId),
    ]).then(([p, ch, pe, ho]) => {
      if (p)  setPartner(p)
      if (ch) setChildren(ch)
      if (pe) setPets(pe)
      if (ho) setHousing(ho)
      setIsLoading(false)
    })
  }, [userId])

  // Autosave per entity - each writes to correct wizard service key
  const savePartnerFn  = useCallback(async (d: PartnerData)  => {
    if (!userId) return
    await savePartner(userId, d)
    const filled = Object.values(d).filter(v => v).length
    await saveModuleProgress(userId, 'intake', filled, 6)
  }, [userId])

  const saveChildrenFn = useCallback(async (d: ChildrenData) => {
    if (userId) await saveChildren(userId, d)
  }, [userId])

  const savePetsFn     = useCallback(async (d: PetsData)     => {
    if (userId) await savePets(userId, d)
  }, [userId])

  const saveHousingFn  = useCallback(async (d: HousingData)  => {
    if (userId) await saveHousing(userId, d)
  }, [userId])

  const { status: partnerStatus,  saveNow: savePartnerNow  } = useAutosave({ data: partner,  onSave: savePartnerFn,  enabled: !isLoading })
  const { status: childrenStatus, saveNow: saveChildrenNow } = useAutosave({ data: children, onSave: saveChildrenFn, enabled: !isLoading })
  const { status: petsStatus,     saveNow: savePetsNow     } = useAutosave({ data: pets,     onSave: savePetsFn,     enabled: !isLoading })
  const { status: housingStatus,  saveNow: saveHousingNow  } = useAutosave({ data: housing,  onSave: saveHousingFn,  enabled: !isLoading })
  // V40C-P1: alle pending saves tegelijk uitvoeren vóór navigatie
  async function saveAllNow() {
    await Promise.all([savePartnerNow(), saveChildrenNow(), savePetsNow(), saveHousingNow()])
  }

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <button onClick={async () => { await saveAllNow(); navigate(-1) }}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
          <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Terug naar vorige scherm
        </button>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-ink mb-1">Persoonlijke gegevens</h1>
        <p className="text-sm text-ink-3">
          Informatie over uw partner, kinderen, huisdieren en woning.
          Data die u hier invult wordt automatisch gesynchroniseerd met de wizard en het nabestaandendossier.
        </p>
      </div>

      <PartnerSection  data={partner}  onChange={setPartner}  status={partnerStatus} />
      <ChildrenSection data={children} onChange={setChildren} status={childrenStatus} />
      <PetsSection     data={pets}     onChange={setPets}     status={petsStatus} />
      <HousingSection  data={housing}  onChange={setHousing}  status={housingStatus} />
      <div className="flex flex-col gap-2 mt-2">
        <PrimaryActionButton onClick={async () => { await saveAllNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
        <SecondaryActionButton onClick={async () => { await saveAllNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
      </div>
    </div>
  )
}
